import { default as CsiFormData, default as FormData } from "csi/action/FormData";
import JsonResponse from "csi/comm/JsonResponse";
import { getSlimCardAttrsAsString } from "csi/middleware/card/SlimCardStore";
import { ControllerSaveFunction } from "csi/middleware/common";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { restoreFromBOM } from "./RestoreFromBOMService";


export interface RestoreFromBOMResources {

	configureRestoreSettings: string;

	restoreSettings: string;

	placementsToDelete: string;

	placementsOnStyleBOM: string;

	placementsToAdd: string;
};

export class RestoreFromBOMStore extends ReactionStore {
	private readonly contextNode: C8Node;

	protected actionResourcePrefix: string;

	readonly actionId: string;

	private readonly cache: ICache = new WorkingCache();

	readonly formData: CsiFormData;

	protected readonly locale: ILocaleProvider;

	private readonly onSave: ControllerSaveFunction;

	private readonly restoreBOMRevisionURL: cnl;

	readonly titleRes: string;

	readonly placementSettingsLabel: string;

	readonly attributeSettingsLabel: string;

	readonly addItemsMap: Map<string, CheckboxTreeNode>;

	@observable.ref filteredAddItems: CheckboxTreeNode[];

	readonly deleteItems: CheckboxTreeNode;

	@observable.ref filteredDeleteItems: CheckboxTreeNode;

	private readonly updateItemsMap: Map<string, CheckboxTreeNode>;

	@observable.ref filteredUpdateItems: CheckboxTreeNode[];

	@observable searchText: string;

	@observable restoreAllPlacements: boolean;

	@observable overrideAttributes: boolean;

	@observable createRevision: boolean;

	resources: RestoreFromBOMResources;

	hasRevision: boolean = false;

	hasOverrideAttributesOption: boolean;

	constructor(
		actionId: string,
		cache: ICache,
		formData: FormData,
		contextNode: C8Node,
		onClose: () => void,
		onSave: ControllerSaveFunction,
		locale: ILocaleProvider,
		resources: RestoreFromBOMResources,
		addItemsMap: Map<string, CheckboxTreeNode>,
		deleteItems: CheckboxTreeNode,
		updateItemsMap: Map<string, CheckboxTreeNode>,
		hasRevision: boolean,
		hasOverrideAttributesOption: boolean,
	) {
		super();
		makeObservable(this);
		this.actionId = actionId;
		this.cache = cache;
		this.locale = locale;
		this.contextNode = contextNode;
		this.restoreBOMRevisionURL = contextNode.$URL;
		this.formData = formData;

		this.addItemsMap = addItemsMap;
		this.filteredAddItems = this.addItems;
		this.deleteItems = deleteItems;
		this.filteredDeleteItems = this.deleteItems;
		this.updateItemsMap = updateItemsMap;
		this.filteredUpdateItems = this.updateItems;
		this.restoreAllPlacements = true;
		this.overrideAttributes = false;
		this.deleteItems?.changeAllChildren(this.restoreAllPlacements);
		this.actionResourcePrefix = actionId;
		this.titleRes = actionId;
		this.resources = resources;
		this.onSave = onSave;
		this.hasRevision = hasRevision;
		this.createRevision = false;
		this.placementSettingsLabel = this.locale.resource("PlacementsSettings");
		this.attributeSettingsLabel = this.locale.resource("AttributesSettings");
		this.hasOverrideAttributesOption = hasOverrideAttributesOption;
	}

	/** Get items to be updated as array */
	@computed get updateItems(): CheckboxTreeNode[] {
		return this.updateItemsMap ? Array.from(this.updateItemsMap.values()) : [];
	}

	/** Get uniqueIds for items to be updated */
	@computed get updateUniqueIds(): string[] {
		return this.filteredUpdateItems.map((item) => item.uniqueId);
	}

	/** Get items to be added as array */
	@computed get addItems(): CheckboxTreeNode[] {
		return this.addItemsMap ? Array.from(this.addItemsMap.values()) : [];
	}

	/** Get uniqueIds for items to be added */
	@computed get addUniqueIds(): string[] {
		return this.filteredAddItems.map((item) => item.uniqueId);
	}

	/**
	 * On save handler
	 */
	public save = async (): Promise<boolean> => {
		const { onSave, onSavePromise } = this;
		onSave(onSavePromise());
		return true;
	};

	/**
	 * Promise to send the request to Restore From BOM
	 */
	public onSavePromise = async (): Promise<JsonResponse> => {
		const { actionId, restoreBOMRevisionURL, filteredAddItems, filteredDeleteItems, restoreAllPlacements, overrideAttributes, filteredUpdateItems } = this;
		try {
			const itemsToUpdate = filteredUpdateItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
			const itemsToAdd = filteredAddItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
			const res = await restoreFromBOM(actionId, restoreBOMRevisionURL, restoreAllPlacements, overrideAttributes, itemsToAdd, itemsToUpdate, filteredDeleteItems);

			return res;
		} catch (err) {
			throw err;
		}
	};

	/** Get the number of Placements selected to be added  */
	@computed get addsSelected(): CheckboxTreeNode[] {
		return this.filteredAddItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
	}

	/** Get the number of Placements selected to be updated  */
	@computed get updatesSelected(): CheckboxTreeNode[] {
		return this.filteredUpdateItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
	}

	/** Get the number of Placements selected */
	@computed get placementsSelected(): number {
		const { addsSelected, filteredDeleteItems, updatesSelected } = this;
		const deletesSelected = filteredDeleteItems.selectedNodes;
		const count = this.restoreAllPlacements ? updatesSelected.length + addsSelected.length + deletesSelected.length : updatesSelected.length + deletesSelected.length;
		return count;
	}

	/** page action message */
	get actionMessage(): string {
		const { actionResource, locale, placementsSelected } = this;
		return locale.resource(actionResource("PlacementsSelected"), [placementsSelected]);
	}
	/**  Utility to get action resource by suffix */
	actionResource = (actionResource: string): string => {
		return `${this.titleRes}_${actionResource}`;
	};

	/**  get the Radio option array. */
	get placementOptions(): RadioOption<boolean>[] {
		const { actionResource, locale } = this;
		return [
			{
				name: locale.resource(actionResource("RestoreAllPlacements")),
				helperTextRes: actionResource("RestoreAllPlacementsHelperText"),
				value: true,
			},
			{
				name: locale.resource(actionResource("OnlyUpdatePlacements")),
				helperTextRes: actionResource("OnlyUpdatePlacementsHelperText"),
				value: false,
			},
		];
	}

	/**  get the Radio option array. */
	get attributeOptions(): RadioOption<boolean>[] {
		const { actionResource, locale } = this;
		if (!this.hasOverrideAttributesOption) {
			return [];
		}
		return [
			{
				name: locale.resource(actionResource("DoNotOverrideAttributeValues")),
				helperTextRes: actionResource("DoNotOverrideAttributeValuesHelperText"),
				value: false,
			},
			{
				name: locale.resource(actionResource("OverrideAttributeValues")),
				helperTextRes: actionResource("OverrideAttributeValuesHelperText"),
				value: true,
			},
		];
	}

	/** If option chosen is 'restore all', select all delete Placements, otherwise deselect */
	@action onChangePlacementOptions = (event: object, value: boolean): void => {
		this.restoreAllPlacements = value;
		this.deleteItems.changeAllChildren(this.restoreAllPlacements);
	};

	@action onChangeAttributeOptions = (event: object, value: boolean): void => {
		this.overrideAttributes = value;
	};

	/** Filter addItems and updateItems */
	getFilteredItems(searchText: string, items: CheckboxTreeNode[]): CheckboxTreeNode[] {
		if (searchText && items.length) {
			const newFilteredItemsList = [];
			items.forEach((node) => {
				const newFilteredItem = CheckboxTreeNode.createCheckboxTreeNode(node, false);
				const filteredNodes = node.children.filter((v) => {
					return getSlimCardAttrsAsString(v.uniqueId, this.cache)
						.toLocaleLowerCase()
						.includes(searchText);
				});
				if (filteredNodes.length) {
					filteredNodes.forEach((e) => {
						newFilteredItem.addChild(e);
					});
					newFilteredItemsList.push(newFilteredItem);
				}
			});
			return newFilteredItemsList;
		}
		return items;
	}

	/* Make search text lower case for use in filtering Placements */
	@action onChangeSearchTextField = (event): void => {
		this.searchText = event.target.value;
		const trimmedText = this.searchText.trim()
			.toLocaleLowerCase();
		/* For delete items */
		if (trimmedText && this.deleteItems.hasChildren) {
			const newFilteredDeleteItems = CheckboxTreeNode.createCheckboxTreeNode(this.deleteItems, false);
			const filteredNodes = this.deleteItems.children.filter((v) => {
				return getSlimCardAttrsAsString(v.uniqueId, this.cache)
					.toLocaleLowerCase()
					.includes(trimmedText);
			});
			filteredNodes.forEach((e) => {
				newFilteredDeleteItems.addChild(e);
			});
			this.filteredDeleteItems = newFilteredDeleteItems;
		} else {
			this.filteredDeleteItems = this.deleteItems;
		}

		this.filteredAddItems = this.getFilteredItems(trimmedText, this.addItems);
		this.filteredUpdateItems = this.getFilteredItems(trimmedText, this.updateItems);
	};

	@action onCreateRevision = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.createRevision = value;
	};
}

export const RestoreFromBOMStoreContext = createContext<RestoreFromBOMStore>(undefined);
export const useRestoreFromBOMStore = (): RestoreFromBOMStore => useContext(RestoreFromBOMStoreContext);